import React, { useState } from 'react';
import axios from 'axios';
import { Recycle, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SoilRemediationContact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    site_location: '',
    contamination_type: '',
    site_size: '',
    contamination_depth: '',
    soil_volume: '',
    remediation_method: '',
    thermal_treatment: '',
    pfas_present: '',
    regulatory_driver: '',
    permit_status: '',
    project_phase: '',
    timeline: '',
    urgency: '',
    specific_requirements: '',
    additional_info: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        subject: `Soil Remediation Inquiry - ${formData.contamination_type}`,
        message: `
Company: ${formData.company}
Phone: ${formData.phone}
Site Location: ${formData.site_location}
Contamination Type: ${formData.contamination_type}
Site Size: ${formData.site_size}
Contamination Depth: ${formData.contamination_depth}
Soil Volume: ${formData.soil_volume}
Remediation Method: ${formData.remediation_method}
Thermal Treatment: ${formData.thermal_treatment}
PFAS Present: ${formData.pfas_present}
Regulatory Driver: ${formData.regulatory_driver}
Permit Status: ${formData.permit_status}
Project Phase: ${formData.project_phase}
Timeline: ${formData.timeline}
Urgency: ${formData.urgency}
Specific Requirements or Concerns: ${formData.specific_requirements}
Additional Information: ${formData.additional_info}
        `.trim(),
        newsletter_signup: false,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your soil remediation project details and contact you within 1-2 business days.',
        });
        setFormData({
          first_name: '',
          last_name: '',
          company: '',
          email: '',
          phone: '',
          site_location: '',
          contamination_type: '',
          site_size: '',
          contamination_depth: '',
          soil_volume: '',
          remediation_method: '',
          thermal_treatment: '',
          pfas_present: '',
          regulatory_driver: '',
          permit_status: '',
          project_phase: '',
          timeline: '',
          urgency: '',
          specific_requirements: '',
          additional_info: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-900 to-emerald-700 px-8 py-12 text-white">
            <div className="flex items-center mb-4">
              <Recycle className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">Soil Remediation & Thermal Treatment Inquiry</h1>
                <p className="text-emerald-100 mt-2">Environmental restoration and contaminated site cleanup</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p>{status.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company/Organization <span className="text-red-500">*</span></label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Site & Contamination Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Location</label>
                    <input type="text" name="site_location" value={formData.site_location} onChange={handleChange} placeholder="City/Region in Alaska" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Contamination Type</label>
                    <select name="contamination_type" value={formData.contamination_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select contamination type</option>
                      <option value="pfas">PFAS (Per- and Polyfluoroalkyl Substances)</option>
                      <option value="petroleum">Petroleum Hydrocarbons</option>
                      <option value="heavy_metals">Heavy Metals (Lead, Arsenic, Mercury)</option>
                      <option value="vocs">Volatile Organic Compounds (VOCs)</option>
                      <option value="chlorinated_solvents">Chlorinated Solvents</option>
                      <option value="mixed_waste">Mixed/Multiple Contaminants</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Size</label>
                    <input type="text" name="site_size" value={formData.site_size} onChange={handleChange} placeholder="e.g., 5 acres, 20,000 sq ft" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contamination Depth</label>
                    <select name="contamination_depth" value={formData.contamination_depth} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select depth</option>
                      <option value="surface">Surface Only (0-2 feet)</option>
                      <option value="shallow">Shallow (2-10 feet)</option>
                      <option value="moderate">Moderate (10-30 feet)</option>
                      <option value="deep">Deep (30+ feet)</option>
                      <option value="unknown">Unknown/Needs Assessment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Soil Volume</label>
                    <input type="text" name="soil_volume" value={formData.soil_volume} onChange={handleChange} placeholder="e.g., 1,000 cubic yards, 500 tons" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Remediation Method</label>
                    <select name="remediation_method" value={formData.remediation_method} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select method</option>
                      <option value="thermal_desorption">Thermal Desorption</option>
                      <option value="thermal_incineration">Thermal Incineration</option>
                      <option value="bioremediation">Bioremediation</option>
                      <option value="excavation_disposal">Excavation & Disposal</option>
                      <option value="in_situ">In-Situ Treatment</option>
                      <option value="soil_washing">Soil Washing</option>
                      <option value="unsure">Not Sure - Need Recommendations</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Thermal Treatment Interest</label>
                    <select name="thermal_treatment" value={formData.thermal_treatment} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select option</option>
                      <option value="yes_primary">Yes - Primary Treatment Option</option>
                      <option value="yes_considering">Yes - Considering</option>
                      <option value="evaluation">Need Technical Evaluation</option>
                      <option value="no">No - Other Methods Preferred</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PFAS Contamination Present?</label>
                    <select name="pfas_present" value={formData.pfas_present} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select option</option>
                      <option value="yes_confirmed">Yes - Confirmed by Testing</option>
                      <option value="yes_suspected">Yes - Suspected</option>
                      <option value="testing_needed">Testing Needed</option>
                      <option value="no">No PFAS</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Regulatory Driver</label>
                    <select name="regulatory_driver" value={formData.regulatory_driver} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select driver</option>
                      <option value="adec_order">ADEC Cleanup Order</option>
                      <option value="epa_action">EPA Enforcement Action</option>
                      <option value="voluntary">Voluntary Cleanup</option>
                      <option value="property_sale">Property Sale/Transfer</option>
                      <option value="brownfields">Brownfields Redevelopment</option>
                      <option value="liability">Liability Reduction</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permit Status</label>
                    <select name="permit_status" value={formData.permit_status} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select status</option>
                      <option value="no_permit">No Permits Yet</option>
                      <option value="need_air_permits">Need Air Quality Permits</option>
                      <option value="thermal_permit">Need Thermal Treatment Permit</option>
                      <option value="nsps_compliance">NSPS Compliance Needed</option>
                      <option value="neshap_compliance">NESHAP Compliance Needed</option>
                      <option value="comprehensive">Need Comprehensive Permitting</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Phase</label>
                    <select name="project_phase" value={formData.project_phase} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select phase</option>
                      <option value="initial_assessment">Initial Site Assessment</option>
                      <option value="characterization">Site Characterization</option>
                      <option value="feasibility">Feasibility Study</option>
                      <option value="design">Remediation Design</option>
                      <option value="ready_to_implement">Ready to Implement</option>
                      <option value="ongoing">Ongoing Remediation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select timeline</option>
                      <option value="immediate">Immediate (Under 1 month)</option>
                      <option value="1-3_months">1-3 months</option>
                      <option value="3-6_months">3-6 months</option>
                      <option value="6-12_months">6-12 months</option>
                      <option value="12+_months">12+ months</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Urgency</label>
                    <select name="urgency" value={formData.urgency} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                      <option value="">Select urgency</option>
                      <option value="critical">Critical - Regulatory Deadline</option>
                      <option value="high">High - Active Contamination</option>
                      <option value="moderate">Moderate - Planned Cleanup</option>
                      <option value="planning">Planning Stage</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                    <textarea name="specific_requirements" value={formData.specific_requirements} onChange={handleChange} required rows="4" placeholder="Treatment goals, cleanup levels, air emissions concerns, stack testing needs, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows="3" placeholder="Any other details about your remediation project..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"></textarea>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-700 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <span>Submitting...</span> : <><span>Submit Soil Remediation Inquiry</span><Send size={20} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoilRemediationContact;
